/**
 * Azure Resource Health — deliberately scoped, and labelled with why.
 *
 * This is the board's supplementary health source, never its primary one.
 * `Microsoft.ResourceHealth` is only registered on some of the estate's
 * subscriptions (the rest answer HTTP 409 `AuthError`) and it does not support
 * Container Apps at all, which is 38 of the workloads. Presenting it as an
 * estate-wide all-clear would be a lie, so the panel names every subscription
 * it does not cover and the reason Azure gave.
 */

import { Badge, DataGrid, EmptyState, Statistic, Typography } from "@norican/react";

import type { ResourceHealthEntry, ResourceHealthPanel as ResourceHealthPayload } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { AVAILABILITY_TONE, formatTimestamp, shortType } from "./status";

interface ResourceHealthProps {
  panel: ResourceHealthPayload;
}

export function ResourceHealth({ panel }: ResourceHealthProps) {
  // Available resources are the boring majority; lead with the ones that aren't.
  const notable = panel.entries.filter((entry) => entry.availability_state !== "Available");

  return (
    <PanelCard
      title="Azure Resource Health"
      caption={`${panel.supported_subscriptions.length} of ${
        panel.supported_subscriptions.length + panel.excluded_subscriptions.length
      } subscriptions supported`}
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic label="Available" value={String(panel.available)} tone="success" />
          <Statistic label="Unavailable" value={String(panel.unavailable)} tone="danger" />
          <Statistic label="Degraded" value={String(panel.degraded)} tone="warning" />
          <Statistic label="No verdict" value={String(panel.unknown)} tone="neutral" />
        </div>

        <Typography variant="body-sm" tone="muted">
          {panel.coverage_note}
        </Typography>

        {notable.length === 0 ? (
          <EmptyState
            size="sm"
            title="Azure reports every covered resource as available"
            description="Nothing in the supported subscriptions is unavailable or degraded."
          />
        ) : (
          <DataGrid<ResourceHealthEntry>
            data={notable}
            getRowId={(entry) => entry.resource_id}
            density="compact"
            aria-label="Resources Azure reports as not available"
            columns={[
              {
                id: "state",
                header: "State",
                accessor: (entry) => entry.availability_state,
                sortable: true,
                cell: (entry) => (
                  <Badge
                    tone={AVAILABILITY_TONE[entry.availability_state] ?? "neutral"}
                    variant="subtle"
                    size="xs"
                  >
                    {entry.availability_state}
                  </Badge>
                ),
              },
              {
                id: "name",
                header: "Resource",
                accessor: (entry) => entry.name,
                sortable: true,
                cell: (entry) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{entry.name}</span>
                    <span className="text-fg-muted text-xs">{shortType(entry.type)}</span>
                  </div>
                ),
              },
              {
                id: "summary",
                header: "Azure's explanation",
                accessor: (entry) => entry.summary ?? entry.title ?? "",
                cell: (entry) => (
                  <div className="flex flex-col">
                    <span className="text-fg text-sm">{entry.title ?? "—"}</span>
                    {entry.summary && (
                      <span className="text-fg-muted text-xs">{entry.summary}</span>
                    )}
                  </div>
                ),
              },
              {
                id: "since",
                header: "Since",
                accessor: (entry) => entry.occurred_at ?? "",
                sortable: true,
                cell: (entry) => (
                  <span className="text-fg-muted text-xs tabular-nums">
                    {formatTimestamp(entry.occurred_at)}
                  </span>
                ),
              },
            ]}
          />
        )}

        {panel.excluded_subscriptions.length > 0 && (
          <div className="flex flex-col gap-2">
            <Typography variant="label" as="h3">
              Not covered by this panel
            </Typography>
            <ul className="flex flex-col gap-1.5">
              {panel.excluded_subscriptions.map((problem) => (
                <li key={problem.subscription_id} className="flex flex-col">
                  <span className="text-fg text-sm">
                    {problem.name}
                    {problem.status_code != null && (
                      <span className="text-fg-muted"> · HTTP {problem.status_code}</span>
                    )}
                  </span>
                  <span className="text-fg-muted text-xs">{problem.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PanelCard>
  );
}
